import { useEffect, useState } from "react";
import { Loader2, TrendingUp } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Monthly analytics panel for the admin dashboard.
 * Renders last-N months of: visits, requests, approvals, declines, deliveries, closed, messages.
 */
export default function MonthlyStats() {
  const [months, setMonths] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get("/admin/stats/monthly?months=12");
        if (active) setMonths(data.months || []);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const max = Math.max(1, ...months.map((m) => m.visits));

  const fmtMonth = (ym) => {
    const [y, m] = ym.split("-");
    return new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1).toLocaleString("en-US", {
      month: "short",
      year: "2-digit",
    });
  };

  return (
    <div
      className="bg-[#18181E] border border-white/10 rounded-2xl p-5 sm:p-6"
      data-testid="monthly-stats-panel"
    >
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] font-semibold text-amber-400">
            / Last 12 Months
          </div>
          <h3 className="font-display text-lg font-bold uppercase tracking-wider mt-1 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-amber-400" /> Monthly Statistics
          </h3>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="w-5 h-5 animate-spin text-amber-400" />
        </div>
      )}

      {!loading && months.length > 0 && (
        <>
          {/* Visits bar chart */}
          <div className="mb-8">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-3">
              Page visits
            </div>
            <div className="flex items-end gap-1.5 h-32">
              {months.map((m) => {
                const h = Math.max(4, Math.round((m.visits / max) * 100));
                return (
                  <div key={m.month} className="flex-1 flex flex-col items-center gap-1.5 group">
                    <div
                      className="w-full rounded-t-md bg-amber-500/40 group-hover:bg-amber-500 transition relative"
                      style={{ height: `${h}%` }}
                      title={`${m.visits} visits`}
                      data-testid={`visits-bar-${m.month}`}
                    >
                      <div className="absolute -top-6 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 bg-black text-amber-300 text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap pointer-events-none transition">
                        {m.visits}
                      </div>
                    </div>
                    <div className="text-[9px] text-slate-500 font-mono-brand uppercase">
                      {fmtMonth(m.month)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Breakdown table */}
          <div className="overflow-x-auto">
            <table
              className="w-full text-sm border-collapse"
              data-testid="monthly-stats-table"
            >
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-white/5">
                  <th className="py-2 pr-3 font-semibold">Month</th>
                  <th className="py-2 pr-3 font-semibold text-right">Visits</th>
                  <th className="py-2 pr-3 font-semibold text-right">New Requests</th>
                  <th className="py-2 pr-3 font-semibold text-right">Messages</th>
                  <th className="py-2 pr-3 font-semibold text-right text-emerald-400">
                    Approved
                  </th>
                  <th className="py-2 pr-3 font-semibold text-right text-red-400">Declined</th>
                  <th className="py-2 pr-3 font-semibold text-right text-blue-400">Delivery</th>
                  <th className="py-2 font-semibold text-right text-zinc-400">Closed</th>
                </tr>
              </thead>
              <tbody>
                {months
                  .slice()
                  .reverse()
                  .map((m) => (
                    <tr
                      key={m.month}
                      className="border-b border-white/5 hover:bg-white/[0.02]"
                      data-testid={`monthly-row-${m.month}`}
                    >
                      <td className="py-2.5 pr-3 font-mono-brand text-amber-400 text-xs">
                        {fmtMonth(m.month)}
                      </td>
                      <td className="py-2.5 pr-3 text-right text-white font-semibold">
                        {m.visits}
                      </td>
                      <td className="py-2.5 pr-3 text-right">{m.requests}</td>
                      <td className="py-2.5 pr-3 text-right">{m.messages}</td>
                      <td className="py-2.5 pr-3 text-right text-emerald-300">{m.approvals}</td>
                      <td className="py-2.5 pr-3 text-right text-red-300">{m.declines}</td>
                      <td className="py-2.5 pr-3 text-right text-blue-300">{m.delivered}</td>
                      <td className="py-2.5 text-right text-slate-300">{m.closed}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!loading && months.length === 0 && (
        <div className="text-center text-sm text-slate-500 py-10">
          No activity yet.
        </div>
      )}
    </div>
  );
}
